import { useContext } from "react";
import { Link } from "react-router-dom";
import { LoginTypesTranslator, personsConstants } from "../api/Person";
import { DetailsPageTopControlsView } from "../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import Status from "../common/badges/Status/Status";
import { SessionContext } from "../common/contexts/SessionContext";
import { NotSet } from "../common/misc/UIconstants";
import Detail from "../common/panels/Detail/Detail";
import { Table } from "../common/panels/Detail/DetailTable";
import { DatasetsTile } from "../common/sidebar/tiles/DatasetsTile";
import { SamplesTile } from "../common/sidebar/tiles/SamplesTile";
import { ProjectPersonPermissions } from "../ProjectPersonPermissions/ProjectPersonPermissions";
import { EntityDetailProps, EntityDetailTableProps } from "../common/entity/EntityInterfaces";
import { ToggleRenderer } from "../common/buttons/ToggleSwitch/ToggleSwitch";
import { CustomTypeDetailRow, CustomTypeRenderer } from "../Customization/CustomTypes/generics/CustomTypeRenderer";
import { CustomFieldReferencesTile } from "../common/sidebar/tiles/CustomFieldReferencesTile";
import { TwoFactorAuthIndicator } from "./TotpModal";
import { EntityRestoreButton } from "../common/entity/entityComponents/EntityRestoreButton";
import { EntitySidebarTileAutosorter } from "../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTileAutosorter";
import { EntityDeleteButton } from "../Entities/GenericComponents/common/GenericEntityDetailPage";
import { EntityEditButton } from "../common/entity/entityComponents/EntityEditButton";
import { EntityCloneButton } from "../common/entity/entityComponents/EntityCloneButton";
import { EntityComplianceDropdown } from "../common/entity/entityComponents/EntityComplianceDropdown";
import { usePost } from "../api/BaseEntityApi";
import { IResultModel } from "../api/GenericTypes";
import { Permission, PermissionFilters } from "../api/Permission";
import { LoadingWrapper } from "../common/LoadingWrapper";
import { GenericEntityExportButton } from "../Entities/GenericComponents/common/GenericEntityExportButton";

export const PersonDetailTable = ({
  entity,
  entityConstants,
  entityTypeId,
  fieldLabels,
  showLinks = true,
}: EntityDetailTableProps<"persons">) => {
  const { session } = useContext(SessionContext);
  const enable2FA = session?.features.enable_2fa;

  const {
    data: permissionsData,
    error: errorPermissions,
    status: statusPermissions,
    fetchStatus: fetchStatusPermissions,
  } = usePost<IResultModel<Permission>>(
    "roles/permissions/list",
    { roleIds: entity.roles?.map((r) => r.id) } as PermissionFilters,
    {
      enabled: !!entity.roles?.length && (!!session?.permissions.logsadmin || session?.permissions.view_admin_pages),
    }
  );

  return (
    <>
      {entity.login && (session?.permissions.administer_persons || session?.userId === entity.id) && (
        <Table noPadding nonFoldable>
          <Table.Head>Account details</Table.Head>
          <Table.Body>
            <Table.Body.RowContent title={fieldLabels.login} content={entity.login} />
            <Table.Body.RowContent
              title={fieldLabels.loginType}
              content={
                !!entity.loginType ? (
                  <label
                    style={{
                      margin: 0,
                      lineHeight: undefined,
                    }}
                  >
                    {LoginTypesTranslator[entity.loginType]}
                  </label>
                ) : (
                  NotSet
                )
              }
            />
            <Table.Body.RowContent
              title={fieldLabels.accountState}
              content={
                <>
                  {!!entity.login ? (
                    <span className="flex row-nowrap align-center gap-5 badge" title={entity.login || ""}>
                      <Status type={entity.accountState === "Disabled" ? "danger" : "success"} />
                      {entity.accountState === "Disabled" ? "Disabled" : "Enabled"}
                    </span>
                  ) : (
                    NotSet
                  )}
                </>
              }
            />
            {(session.permissions.logsadmin || session.permissions.view_admin_pages) && (
              <Table.Body.RowContent
                title={fieldLabels.isSystemUser}
                content={<ToggleRenderer value={!!entity.isSystemUser} />}
              />
            )}
            <Table.Body.RowContent
              title={fieldLabels.roles}
              content={
                <div
                  style={{
                    display: "flex",
                    flexFlow: "row wrap",
                    gap: "5px",
                    overflow: "auto",
                    alignItems: "center",
                  }}
                >
                  {entity.roles?.map((c, i) => (
                    <span key={i} className="label label-soft-secondary">
                      {c.name}
                    </span>
                  )) || NotSet}
                </div>
              }
            />
            {(session.permissions.logsadmin || session.permissions.view_admin_pages) && (
              <LoadingWrapper status={statusPermissions} fetchStatus={fetchStatusPermissions} error={errorPermissions}>
                <Table.Body.RowContent
                  title={fieldLabels.permissions + " (admin only)"}
                  content={
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "row wrap",
                        gap: "5px",
                        overflow: "auto",
                        alignItems: "center",
                      }}
                    >
                      {permissionsData?.results.map((c, i) => (
                        <span key={i} className="label label-soft-secondary">
                          {c.name}
                        </span>
                      )) || NotSet}
                    </div>
                  }
                />
              </LoadingWrapper>
            )}
            {enable2FA && entity.loginType && (
              <Table.Body.RowContent
                title={fieldLabels.has2FA}
                content={<TwoFactorAuthIndicator enabled={!!entity.has2FA} />}
              />
            )}
          </Table.Body>
        </Table>
      )}

      <Table noPadding nonFoldable>
        <Table.Head>Person details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className="container_label">
                <div className="container_label_name" style={{ fontWeight: 600 }}>
                  {entity.name ?? NotSet}
                </div>
                <div className="container_label_id">
                  <span style={{ whiteSpace: "nowrap" }}>Person-ID: {entity.id}</span>
                </div>
              </div>
            }
          />

          <CustomTypeDetailRow entity={entity} entityTypeId={entityTypeId} showLinks={showLinks} />

          <Table.Body.RowContent
            title={fieldLabels.email}
            content={
              entity.email ? (
                <Link
                  to="#"
                  onClick={(e) => {
                    window.location.href = `mailto:${entity.email}`;
                    e.preventDefault();
                  }}
                >
                  {entity.email}
                </Link>
              ) : (
                NotSet
              )
            }
          />
        </Table.Body>
      </Table>
      <CustomTypeRenderer entity={entity} showLinks={showLinks} />
    </>
  );
};

export const PersonsDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"persons">) => {
  const { session } = useContext(SessionContext);

  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={personsConstants}
          controls={
            <>
              <EntityRestoreButton
                entity={entity}
                entityConstants={entityConstants}
                entityApi={entityApi}
                permissions={permissions}
              />
              <EntityEditButton
                entity={entity}
                entityConstants={entityConstants}
                permissions={permissions}
                routes={routes}
              />
            </>
          }
          controlsDropdown={
            <>
              <li>
                <EntityCloneButton
                  entity={entity}
                  entityConstants={entityConstants}
                  routes={routes}
                  permissions={permissions}
                />
              </li>
              <li>
                <GenericEntityExportButton entity={entity} entityTypeId={entityTypeId} />
              </li>
              <EntityComplianceDropdown entity={entity} entityTypeId={entityTypeId} />
              <li className="divider" />
              <li>
                <EntityDeleteButton entity={entity} entityTypeId={entityTypeId} />
              </li>
            </>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <>
          <PersonDetailTable
            entity={entity}
            entityConstants={entityConstants}
            fieldLabels={fieldLabels}
            entityTypeId={entityTypeId}
          />
          {(session?.permissions.administer_projects || session?.userId === entity.id) && !!entity.login && (
            <Table>
              <Table.Head>{fieldLabels.projects}</Table.Head>
              <Table.Body>
                <ProjectPersonPermissions person={entity} />
              </Table.Body>
            </Table>
          )}
        </>
      }
      sidebar={
        <EntitySidebarTileAutosorter>
          <DatasetsTile
            defaultFilters={{ ownerIds: [entity.id], isViewableEntity: false }}
            excludeFilters={{ ownerIds: () => false, isViewableEntity: () => false }}
          />
          <SamplesTile defaultFilters={{ enteredByIds: [entity.id] }} excludeFilters={{ enteredByIds: () => false }} />

          {/* {session?.features.eln && (
            <>
              {!!person.relations?.labNotebooks.count && (
                <LabNotebooksTile
                  defaultFilters={{ createdByIds: [person.id] }}
                  excludeFilters={{ createdByIds: () => false }}
                />
              )}
              {!!person.relations?.labNotebooksEntryMentions.count && (
                <LabNotebookEntriesTile
                  title="Notebook mentions"
                  defaultFilters={{ personIds: [person.id] }}
                  excludeFilters={{ personIds: () => false }}
                />
              )}
            </>
          )} */}
          <CustomFieldReferencesTile
            entityType="Person"
            entity={entity}
            labNotebookFilters={session?.features.eln ? { enteredByIds: [entity.id] } : undefined}
            labNotebookEntryFilters={session?.features.eln ? { personReferenceIds: [entity.id] } : undefined}
          />
          {/* <AttachmentsTileWrapped entity={person} /> */}

          {/* {session?.features.eln && (
            <>
              {!person.relations?.labNotebooks.count && <LabNotebooksTile disabled />}
              {!person.relations?.labNotebooksEntryMentions.count && (
                <LabNotebookEntriesTile disabled title="Notebook mentions" />
              )}
            </>
          )} */}
        </EntitySidebarTileAutosorter>
      }
    />
  );
};
